import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { downloadFileApi } from "@services/common/common.api";
import { Viewer } from "@services/task/task.service";
import { PluginReactContext } from "molstar/lib/mol-plugin-ui/base";
// import { SequenceView } from "molstar/lib/mol-plugin-ui/sequence";
import { SequenceView } from "molstar/lib/custom/light/sequence";
import { PluginCommands } from "molstar/lib/mol-plugin/commands";
import { StateTransform } from "molstar/lib/mol-state";
import { readFileContent } from "@utils/file.util";
import { getFileName } from "@utils/file.util";
import { PluginUIContext } from "molstar/lib/mol-plugin-ui/context";
import { SelectionToolsHideModel } from "@services/task/model/task.model";
// import { StateTree } from "molstar/lib/mol-plugin-ui/state/tree";
import styles from "./index.module.scss";
import classNames from "classnames";

interface Prop {
  children?: React.ReactNode;
  style?: Record<string, string>;
  sequenceTitle?: string;
  from?: string;
  showCopy?: boolean;
  pluginChange?: (plugin: PluginUIContext) => void;
  fileLoaded?: (ref: string) => void;
  showViewportControl?: boolean;
  selectionToolsHideConfig?: SelectionToolsHideModel;
  showAnimation?: boolean;
}

const StructureViewer = forwardRef<any, Prop>(
  (
    {
      pluginChange,
      fileLoaded,
      children,
      style,
      from,
      sequenceTitle,
      showCopy,
      showViewportControl = true,
      selectionToolsHideConfig = {
        granularity: false,
        union: false,
        subtract: false,
        intersect: false,
        set: false,
        theme: false,
        componentAdd: false,
        componentRemove: false,
        undo: false,
        help: false,
        cancel: false,
      },
      showAnimation = true,
    },
    ref
  ) => {
    const viewerRef = useRef<Viewer>();

    const sequencePanelRef = useRef<SequenceView | null>(null);
    const [plugin, setPlugin] = useState<PluginUIContext>();
    const [viewer, setViewer] = useState<Viewer>();

    function clearStructure() {
      if (!plugin) {
        return;
      }
      PluginCommands.State.RemoveObject(plugin, {
        state: plugin.state.data,
        ref: StateTransform.RootRef,
      });
    }

    useEffect(() => {
      Viewer.create("viewer", {
        layoutIsExpanded: false,
        disabledExtensions: [], // anything from Object.keys(molstar.ExtensionMap)
        layoutShowControls: false,
        viewportShowExpand: false,
        collapseLeftPanel: false,
        pdbProvider: "pdbe",
        emdbProvider: "pdbe",
        volumeStreamingServer: "https://www.ebi.ac.uk/pdbe/densities",
        pixelScale: 1,
        pickScale: 0.25,
        //   pickPadding: 1,
        transparency: undefined,
        preferWebgl1: undefined,
        allowMajorPerformanceCaveat: undefined,
        powerPreference: "high-performance",
        components: {
          selectionTools: {
            hide: selectionToolsHideConfig,
            // controls: showSelectionToolsControl ? undefined : () => null,
          },
          viewport: {
            controls: showViewportControl ? undefined : () => null,
          },
          disableDragOverlay: true,
        },
        viewportShowAnimation: showAnimation,
      }).then((v) => {
        setViewer(v);
        viewerRef.current = v;
        // v.loadStructureFromUrl(
        // "https://www.ebi.ac.uk/pdbe/entry-files/download/7sbu.cif",
        // "https://www.ebi.ac.uk/pdbe/entry-files/download/7sba.cif",
        //   "https://www.ebi.ac.uk/pdbe/entry-files/download/6xc3.cif",
        //   "mmcif",
        //   undefined
        // );

        if (pluginChange) {
          pluginChange(v.plugin);
        }

        setPlugin(v.plugin);
      });
    }, []);

    useImperativeHandle(ref, () => ({
      async loadStructure(
        fileName: string,
        id: string,
        isEditMode = false
      ): Promise<string> {
        if (!viewer) return "";
        clearStructure();

        if (isEditMode) {
          viewer.plugin.selectionMode = true;
        }

        const content = await downloadFileApi([id], "text");
        const label = fileName.toLocaleLowerCase();
        const extension = label.split(".").pop();
        const val = await viewer.loadStructureFromData(
          content,
          extension === "cif" ? "mmcif" : "pdb",
          {
            dataLabel: getFileName(fileName, true),
          }
        );

        // if (val) {
        //   updateTheme(viewer.plugin, val.structure.ref);
        // }

        if (val && fileLoaded) {
          fileLoaded(val.structures[0].ref);
        }

        if (val) {
          return val.structures[0].ref;
        } else {
          return "";
        }
      },
      async loadStructureFromFile(
        file: File,
        isEditMode = false
      ): Promise<string> {
        clearStructure();

        if (!viewerRef.current) return "";

        if (isEditMode) {
          viewerRef.current.plugin.selectionMode = true;
        }

        try {
          const content = await readFileContent(file);

          const label = file.name.toLocaleLowerCase();
          const extension = label.split(".").pop();

          const val = await viewerRef.current.loadStructureFromData(
            content,
            extension === "cif" ? "mmcif" : "pdb",
            {
              dataLabel: file.name,
            }
          );

          // if (val && viewer) {
          //   updateTheme(viewer.plugin, val.structure.ref);
          // }

          if (val && fileLoaded) {
            fileLoaded(val.structures[0].ref);
          }

          if (val) {
            return val.structures[0].ref;
          } else {
            return "";
          }
        } catch (e) {
          return "";
        }
      },
      clearStructure() {
        clearStructure();
      },
      getPlugin(): PluginUIContext | undefined {
        return plugin;
      },
      getSequenceWrappers() {
        return sequencePanelRef.current?.sequenceWrappers;
      },
    }));

    useEffect(() => {
      const viewerDiv = document.getElementById("viewer");

      function stopDefault(e: MouseEvent) {
        e.stopPropagation();
        e.preventDefault();
      }
      viewerDiv?.addEventListener("click", stopDefault);
      return () => {
        viewerDiv?.removeEventListener("click", stopDefault);
      };
    }, []);

    return (
      <div className={styles.container}>
        {/*  eslint-disable */}
        <PluginReactContext.Provider value={plugin as any}>
          {/* eslint-enable */}
          <div
            className={classNames({
              "msp-plugin": true,
              [styles.row]: true,
              [styles.dialog]: from === "dialog",
            })}
          >
            {/* <div
              style={{
                height: "100%",
                overflow: "auto",
                width: "300px",
              }}
            >
              {plugin && <StateTree state={plugin.state.data} />}
            </div> */}
            <div className={styles.sequenceContainer}>
              {plugin && (
                <SequenceView
                  ref={sequencePanelRef}
                  styles={style}
                  defaultMode="polymers"
                  sequenceTitle={sequenceTitle}
                  showCopy={showCopy}
                  showOption={false}
                />
              )}
              {children && <div>{children}</div>}
            </div>
            <div className={styles.viewerContainer}>
              <div id="viewer"></div>
            </div>
          </div>
        </PluginReactContext.Provider>
      </div>
    );
  }
);

StructureViewer.displayName = "StructureViewer";

export default StructureViewer;
